import { AmountCents, DocumentMetadata, EntityError, ENVIRONMENT, LANGUAGE_CODE } from "./generic"

export enum PAYMENT_METHOD {
  AMEX = "amex",
  BANK_TRANSFER = "bank-transfer",
  DEFAULT = "default",
  AMERICAN_EXPRESS = "american_express",
  APPLE_PAY = "apple_pay",
  SATISPAY = "satispay",
  MYBANK = "mybank",
  IDEAL = "ideal",
  BANCONTACT = "bancontact",
  API_PARTNER = "api_partner",
}

export enum PURCHASE_STATUS {
  PENDING = "pending",
  PAID = "paid",
  CANCELLED = "cancelled",
  REFUNDED = "refunded"
}

export interface InsuredPerson {
  firstname: string // description: "First name of the insured person", example: "Jane"
  lastname: string //  description: "Last name of the insured person", example: "Doe"
}

export type InsuredDate = string // description: "Date of the insured activity in DD/MM/YYYY format", example: "25/12/2023"

export interface Buyer {
  email: string
  firstname: string
  lastname: string
  type: "person" | "company"
  company_name?: string
  billing_number?: string
  billing_street?: string
  billing_city?: string
  billing_zip?: string
  billing_address_extra?: string
  billing_country?: string
}

export interface Voucher {
  all_products?: boolean
  code: string
  discount: number
  products?: string[]
  single_use: boolean
  use_count?: number
  availability_start?: number
  availability_end?: number
  max_uses?: number
}

export interface PricingSnapshot {
  currency: string
  public_price: AmountCents // in cents - from local product
  public_price_after_discount: AmountCents // in cents - from local product after applying voucher discount
  price_ht: AmountCents // in cents - from local product
  price_ht_after_discount: AmountCents // in cents - from local product after applying voucher discount
  insurance_tax: AmountCents // in cents
  insurance_tax_after_discount: AmountCents // in cents
  share_insurer: AmountCents // in cents - from specific product
  share_broker: AmountCents // in cents - from specific product
  share_co_broker: AmountCents // in cents - from specific product
  share_operator: AmountCents // in cents
  share_club: AmountCents // in cents
  share_other: AmountCents // in cents
  share_webapp: AmountCents // in cents
  voucher_snapshot: Voucher | null
}

export interface PaymentRequest {
  id?: string
  amount: AmountCents
  billing: {
    last_name: string
    first_name: string
    email: string
  }
  buyer: Buyer,
  shipping: {
    last_name: string
    first_name: string
    email: string
  }
  card?: {
    brand: string | null // Card brand (e.g. Visa Mastercard)
    country: string // Country of the card
    exp_month: number // Expiration month of the card
    exp_year: number // Expiration year of the card
    last4: string // Last 4 digits of the card number
  }
  currency: string
  user: string
  payment_method: PAYMENT_METHOD
  reminder_sent: boolean
  purchase_order_url?: string
  status: PURCHASE_STATUS
  paid_at?: number
  is_refunded?: boolean
  meta: {
    created: number
    updated: number
  }
  is_paid?: boolean
  language?: LANGUAGE_CODE,
  reminders?: {
    type: "automatic" | "manual"
    sent_at: number // timestamp
  }[]
  dates: number[] // timestamps
  dates_formatted: string[] // formatted dates - DD/MM/YYYY
  domain: string // domain ID
  duration: string // duration ID
  resort: string // resort ID
  product_generic: string // generic product ID
  product_specific: string // specific product ID
  product_local: string // local product ID
  product_name: string
  people: InsuredPerson[]
  voucher_code?: string
  api_partner?: string
  api_purchase?: boolean
  cg: string // terms and conditions document URL
  ipid: string // IPID document URL
  hosted_payment: boolean
  hosted_payment_url?: string
  hosted_payment_return_url?: string
  hosted_payment_cancel_url?: string
  notification_url?: string
  payment_id?: string
  cancelled_at?: number
  environment: ENVIRONMENT
  pricing_snapshot: PricingSnapshot
  voucher?: Voucher
}

export interface Purchase {
  id: string
  amount: AmountCents
  apple_wallet_pass_url?: string
  buyer: Buyer
  cg: string // terms and conditions document URL
  contract_url?: string // URL to the contract document
  dates: number[] // timestamps
  dates_formatted: string[] // formatted dates - DD/MM/YYYY
  domain: string // domain ID
  duration: string // duration ID
  environment: ENVIRONMENT
  google_wallet_pass_url?: string
  invoice_url: string // URL to the invoice document
  ipid: string // IPID document URL
  language: LANGUAGE_CODE // language code - en fr es it
  meta: DocumentMetadata
  paid_at?: number // timestamp
  payment_id: string // payplug payment ID
  payment_method: PAYMENT_METHOD // amex bank-transfer default (card)
  people: InsuredPerson[]
  product?: string // product ID
  product_generic?: string // generic product ID
  product_local?: string // local product ID
  product_name: string
  product_specific?: string // specific product ID
  purchase_metadata?: {
    activity?: string
    region?: string
  }
  proofs_of_purchase: string[] // URLs to the proofs of purchase
  resort?: string // resort ID
  status: PURCHASE_STATUS // pending paid refunded
  user: string // user ID
  voucher: Voucher
  voucher_code: string,
  hosted_payment?: boolean
  api_purchase?: boolean
  api_partner?: string,
  errors?: EntityError[],
  pricing_snapshot: PricingSnapshot | null
  amount_refunded?: AmountCents // in cents
}
